// src/components/Cockpit/HermesThinking.tsx
// ============================================
// HermesThinking - AI 思考过程实时展示（Cockpit 2.0 / Sprint 3 Task 5）
//
// 设计理念：让 AI 的思考过程"透明可见"
//
// 不是简单的聊天框，而是实时展示 Hermes 的思考链：
//   ✓ 搜索知识
//   ✓ 调用 Workflow
//   ✓ 调用 Planner
//   ✓ 调用 Writer
//   ✓ 调用 Publisher
//   ✓ 更新 Memory
//
// 视觉：
//   - 时间线样式，每个步骤一个节点
//   - 进行中的步骤有脉冲动画
//   - 步骤间有数据流连线
//   - 顶部：当前思考主题 + 已用时长
//   - 底部：当前步骤详情
// ============================================

import { motion, AnimatePresence } from "framer-motion";
import {
  Brain,
  CheckCircle2,
  Loader2,
  Sparkles,
  ArrowRight,
  Target,
  Cpu,
  Eye,
  type LucideIcon,
} from "lucide-react";

// ============================================
// 类型定义
// ============================================

export type ThinkingStatus = "pending" | "running" | "completed" | "failed";

export interface ThinkingStep {
  id: string;
  name: string;
  icon: LucideIcon;
  status: ThinkingStatus;
  /** 步骤描述 */
  description?: string;
  /** 耗时（毫秒） */
  duration?: number;
  /** 强调色 */
  color: string;
}

export interface ThinkingSession {
  /** 当前思考主题 */
  topic: string;
  /** 开始时间 ISO */
  startedAt: string;
  /** 已完成步骤数 */
  completedSteps: number;
  /** 总步骤数 */
  totalSteps: number;
  /** 步骤列表 */
  steps: ThinkingStep[];
}

// ============================================
// 默认数据（空状态，等待 Hermes 真实任务接入）
// ============================================

export const DEFAULT_THINKING_SESSION: ThinkingSession = {
  topic: "",
  startedAt: new Date().toISOString(),
  completedSteps: 0,
  totalSteps: 0,
  steps: [],
};

// ============================================
// 状态 → 图标 + 颜色
// ============================================

function StatusIndicator({
  status,
  color,
}: {
  status: ThinkingStatus;
  color: string;
}) {
  switch (status) {
    case "completed":
      return <CheckCircle2 size={12} style={{ color }} />;
    case "running":
      return (
        <motion.span
          animate={{ rotate: 360 }}
          transition={{ duration: 1.2, repeat: Infinity, ease: "linear" }}
        >
          <Loader2 size={12} style={{ color }} />
        </motion.span>
      );
    case "failed":
      return <span className="text-[10px] font-mono text-red-400">✕</span>;
    default:
      return (
        <span
          className="w-2 h-2 rounded-full border"
          style={{ borderColor: `${color}40` }}
        />
      );
  }
}

// ============================================
// 单个思考步骤
// ============================================

function ThinkingStepView({
  step,
  index,
  isLast,
}: {
  step: ThinkingStep;
  index: number;
  isLast: boolean;
}) {
  const Icon = step.icon;
  const isRunning = step.status === "running";
  const isCompleted = step.status === "completed";

  return (
    <motion.div
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3, delay: 0.1 + index * 0.05 }}
      className="relative flex items-start gap-2"
    >
      {/* 节点 */}
      <div className="relative flex-shrink-0">
        <div
          className="relative w-7 h-7 rounded-md flex items-center justify-center"
          style={{
            background: isCompleted
              ? `${step.color}20`
              : isRunning
              ? `${step.color}30`
              : "rgba(255,255,255,0.02)",
            border: `1px solid ${step.color}${isCompleted || isRunning ? "60" : "20"}`,
            boxShadow: isRunning ? `0 0 12px ${step.color}80` : "none",
          }}
        >
          <Icon size={13} style={{ color: step.color }} />
          {/* 运行中呼吸光晕 */}
          {isRunning && (
            <motion.div
              className="absolute inset-0 rounded-md pointer-events-none"
              style={{ border: `1px solid ${step.color}` }}
              animate={{ opacity: [0.4, 1, 0.4], scale: [1, 1.1, 1] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
            />
          )}
        </div>
        {/* 连接线 */}
        {!isLast && (
          <div
            className="absolute left-1/2 top-7 w-px h-full -translate-x-1/2"
            style={{
              background: isCompleted
                ? `linear-gradient(180deg, ${step.color}60, ${step.color}20)`
                : "rgba(255,255,255,0.05)",
            }}
          />
        )}
      </div>

      {/* 内容 */}
      <div className="flex-1 min-w-0 pb-3">
        <div className="flex items-center gap-2">
          <span
            className="text-[11px] font-mono font-semibold truncate"
            style={{ color: isCompleted || isRunning ? step.color : "#9CA3AF" }}
          >
            {step.name}
          </span>
          <StatusIndicator status={step.status} color={step.color} />
          {step.duration && (
            <span className="text-[9px] font-mono text-gray-600">
              {step.duration}ms
            </span>
          )}
        </div>
        {step.description && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 + index * 0.05 }}
            className="text-[10px] text-gray-500 mt-0.5 leading-tight"
          >
            {step.description}
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}

// ============================================
// HermesThinking 容器
// ============================================

interface HermesThinkingProps {
  session?: ThinkingSession;
  /** 真实意图（来自 HermesRuntime 意图路由） */
  intent?: string;
  /** 真实运行时（来自 HermesRuntime 路由分发） */
  runtime?: string;
  /** 路由目标视图（来自 HermesRuntime 路由分发） */
  view?: string;
}

export default function HermesThinking({
  session = DEFAULT_THINKING_SESSION,
  intent,
  runtime,
  view,
}: HermesThinkingProps) {
  const elapsedSec = Math.floor(
    (Date.now() - new Date(session.startedAt).getTime()) / 1000
  );
  const progressPct =
    session.totalSteps === 0
      ? 0
      : (session.completedSteps / session.totalSteps) * 100;

  const currentStep = session.steps.find((s) => s.status === "running");
  const isEmpty = session.steps.length === 0;
  // 是否有真实路由信息（intent/runtime/view 至少一个）
  const hasRouting = !!(intent || runtime || view);

  return (
    <div className="relative rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-md p-4 h-full flex flex-col overflow-hidden">
      {/* 顶部光带 */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-sky-500/40 to-transparent" />

      {/* 头部 */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-1 h-4 bg-gradient-to-b from-sky-400 to-sky-600 rounded-full" />
          <h3 className="text-sm font-medium text-white">Hermes Thinking</h3>
          <motion.span
            className="flex items-center gap-1 text-[10px] font-mono text-sky-400"
            animate={{ opacity: [1, 0.5, 1] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            <Sparkles size={10} />
            Live
          </motion.span>
        </div>
        {!isEmpty && (
          <span className="text-[10px] font-mono text-gray-500">
            {elapsedSec}s elapsed
          </span>
        )}
      </div>

      {/* 意图路由可视化（当 intent/runtime/view 可用时显示） */}
      {hasRouting && (
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="mb-3 p-2.5 rounded-md bg-white/[0.02] border border-white/5"
        >
          <div className="text-[9px] font-mono uppercase tracking-wider text-gray-500 mb-2">
            意图路由 · Intent Routing
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {/* 意图节点 */}
            {intent && (
              <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-sky-500/10 border border-sky-500/25">
                <Target size={10} className="text-sky-400" />
                <span className="text-[10px] font-mono text-sky-300">{intent}</span>
              </div>
            )}
            {/* 箭头：意图 → 运行时 */}
            {intent && runtime && (
              <ArrowRight size={11} className="text-gray-600" />
            )}
            {/* 运行时节点 */}
            {runtime && (
              <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-purple-500/10 border border-purple-500/25">
                <Cpu size={10} className="text-purple-400" />
                <span className="text-[10px] font-mono text-purple-300">{runtime}</span>
              </div>
            )}
            {/* 箭头：运行时 → 视图 */}
            {runtime && view && (
              <ArrowRight size={11} className="text-gray-600" />
            )}
            {/* 视图节点 */}
            {view && (
              <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-500/10 border border-emerald-500/25">
                <Eye size={10} className="text-emerald-400" />
                <span className="text-[10px] font-mono text-emerald-300">{view}</span>
              </div>
            )}
          </div>
        </motion.div>
      )}

      {/* 空状态：等待 Hermes 执行任务（有路由信息时显示路由已解析） */}
      {isEmpty ? (
        hasRouting ? (
          // 有路由信息：显示路由已解析的简洁状态
          <div className="flex-1 flex flex-col items-center justify-center gap-2 py-6">
            <motion.div
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
            >
              <CheckCircle2 size={18} className="text-emerald-400/80" />
            </motion.div>
            <div className="text-[11px] font-mono text-gray-400">
              路由已解析 · 等待执行
            </div>
            <div className="text-[9px] font-mono text-gray-600">
              意图已分发至目标运行时
            </div>
          </div>
        ) : (
          // 无路由信息：显示等待状态 + 骨架屏
          <div className="flex-1 flex flex-col items-center justify-center gap-3 py-6">
            <motion.div
              animate={{ opacity: [0.4, 1, 0.4] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
            >
              <Brain size={20} className="text-sky-400/70" />
            </motion.div>
            <div className="text-[11px] font-mono text-gray-400">
              等待 Hermes 执行任务...
            </div>
            <div className="text-[9px] font-mono text-gray-600">
              任务启动后将实时展示思考链
            </div>
            {/* 骨架屏占位 */}
            <div className="w-full mt-2 space-y-2">
              {[0, 1, 2].map((i) => (
                <motion.div
                  key={i}
                  className="flex items-center gap-2"
                  animate={{ opacity: [0.2, 0.5, 0.2] }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    delay: i * 0.2,
                    ease: "easeInOut",
                  }}
                >
                  <div className="w-7 h-7 rounded-md bg-white/[0.04]" />
                  <div className="flex-1 space-y-1">
                    <div className="h-2 w-1/3 rounded bg-white/[0.04]" />
                    <div className="h-1.5 w-2/3 rounded bg-white/[0.03]" />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )
      ) : (
        <>
          {/* 当前思考主题 */}
          <div className="mb-3 p-2 rounded-md bg-white/[0.02] border border-white/5">
            <div className="text-[9px] font-mono uppercase tracking-wider text-gray-500 mb-0.5">
              Current Topic
            </div>
            <div className="text-[12px] text-white font-medium truncate">
              {session.topic}
            </div>
          </div>

      {/* 进度条 */}
      <div className="mb-3">
        <div className="flex items-center justify-between text-[9px] font-mono text-gray-500 mb-1">
          <span>Progress</span>
          <span>
            <span className="text-emerald-400 font-bold">
              {session.completedSteps}
            </span>
            <span className="text-gray-600"> / </span>
            <span>{session.totalSteps}</span>
            <span className="text-gray-600 ml-1">steps</span>
          </span>
        </div>
        <div className="h-1 bg-white/5 rounded-full overflow-hidden">
          <motion.div
            className="h-full rounded-full"
            style={{
              background: "linear-gradient(90deg, #34D399, #38BDF8, #A78BFA)",
              boxShadow: "0 0 8px rgba(56,189,248,0.6)",
            }}
            initial={{ width: 0 }}
            animate={{ width: `${progressPct}%` }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          />
        </div>
      </div>

      {/* 思考步骤时间线 */}
      <div className="flex-1 overflow-y-auto pr-1">
        <AnimatePresence>
          {session.steps.map((step, idx) => (
            <ThinkingStepView
              key={step.id}
              step={step}
              index={idx}
              isLast={idx === session.steps.length - 1}
            />
          ))}
        </AnimatePresence>
      </div>

      {/* 底部：当前步骤详情 */}
      {currentStep && (
        <motion.div
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-2 pt-2 border-t border-white/5"
        >
          <div className="text-[9px] font-mono uppercase tracking-wider text-gray-500 mb-0.5">
            Now Executing
          </div>
          <div className="flex items-center gap-2">
            <motion.span
              className="w-1.5 h-1.5 rounded-full"
              style={{ background: currentStep.color }}
              animate={{ scale: [1, 1.8, 1], opacity: [1, 0.4, 1] }}
              transition={{ duration: 1, repeat: Infinity }}
            />
            <span className="text-[11px] font-mono text-white truncate">
              {currentStep.name}
            </span>
          </div>
        </motion.div>
      )}
        </>
      )}
    </div>
  );
}
